import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import toastr from 'react-hot-toast';
import { INFINITE_CACHE_AND_STALE_TIME } from 'constants/index';
import { generateURL } from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type {
  SubscriptionCreditOptions,
  SubscriptionCreditOptionsResponse,
} from 'types/entityTypes/SubscriptionCreditOptions';
import React from 'react';

const getSubscriptionCreditOptions = () => {
  const url = generateURL(SPARK_RESOURCE_TYPES.SUBSCRIPTION_CREDIT_OPTIONS);
  return fetchWrapper<SubscriptionCreditOptionsResponse>({
    url: url,
    method: 'GET',
  });
};

export function useSubscriptionCreditOptions() {
  const response = useQuery(
    ['subscriptionCreditOptions'],
    () => getSubscriptionCreditOptions(),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(
            `failed to Fetch subscription credit options: ${error.message}`
          );
        }
      },
      ...INFINITE_CACHE_AND_STALE_TIME,
    }
  );

  const { data: subscriptionCreditOptions } = response.data || {};

  const groupedIntervalID = React.useMemo<
    { [intervalID: string]: SubscriptionCreditOptions[] } | undefined
  >(() => {
    return subscriptionCreditOptions?.reduce((acc, curr) => {
      const intervalID = curr.attributes.subscriptionBillingIntervalID;
      if (intervalID === null) {
        if (!acc[intervalID]) {
          acc['Free'] = [];
        }
        acc['Free'].push(curr);
        return acc;
      }
      if (!acc[intervalID]) {
        acc[intervalID] = [];
      }
      acc[intervalID].push(curr);
      return acc;
    }, {} as { [intervalID: string]: SubscriptionCreditOptions[] });
  }, [subscriptionCreditOptions]);

  return {
    subscriptionCreditOptions,
    groupedBySubscriptionBillingIntervalID: groupedIntervalID,
    ...response,
  };
}
